"use client";

import Link from "next/link";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";
import { getConsent, onConsentChange, setConsent } from "@/lib/consent";

/**
 * Cookie/consent banner (ePrivacy + GDPR), two-tier.
 *
 * It does NOT gate analytics on/off. An anonymous, cookie-free basic reach
 * measurement already runs for every visitor without consent (nothing stored on
 * the device — see PostHogProvider). This banner only decides the PERSISTENT
 * tier: "Accept" upgrades to on-device cookies (cross-session insight) + session
 * replay; "Reject" opts analytics out entirely. "Reject" is exactly as easy as
 * "Accept" (no dark pattern). Shown only until a choice is made; re-openable
 * from the footer's "Cookie settings" (resetConsent dispatches null).
 */
export function CookieConsent() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    setShow(getConsent() === null);
    return onConsentChange((v) => setShow(v === null));
  }, []);

  // CC-655 (port of finseats T-281): the banner is fixed and floats over the
  // bottom of the page, so at max scroll it covered the footer links on
  // phones. Expose its measured height so layout.tsx reserves exactly that
  // much space — 0 the moment it is dismissed or never shown.
  const bannerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = bannerRef.current;
    if (!show || !el) {
      document.documentElement.style.setProperty("--cookie-banner-h", "0px");
      return;
    }
    const update = () =>
      document.documentElement.style.setProperty("--cookie-banner-h", `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      document.documentElement.style.setProperty("--cookie-banner-h", "0px");
    };
  }, [show]);

  if (!show) return null;

  const choose = (v: "granted" | "denied") => {
    // Record the shared decision (the PostHogProvider listener also reacts to
    // this); apply it to posthog directly too, so the effect is immediate even
    // on the first paint and regardless of listener timing.
    setConsent(v);
    try {
      if (v === "granted") {
        // Upgrade from the default cookieless/in-memory layer to PERSISTENT
        // analytics now that we have consent: on-device storage + replay on.
        posthog.set_config({
          persistence: "localStorage+cookie",
          disable_persistence: false,
        });
        posthog.opt_in_capturing();
        posthog.startSessionRecording?.();
      } else {
        // Opt out fully + clear any stale posthog storage on the device.
        posthog.opt_out_capturing();
        posthog.reset(true);
      }
    } catch {
      /* posthog not loaded (no token) — nothing to configure */
    }
    setShow(false);
  };

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 sm:px-4 sm:pb-4"
    >
      <div className="mx-auto max-w-3xl overflow-hidden rounded-xl border-2 border-ink bg-white shadow-lg">
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-5 sm:p-5">
          <div className="min-w-0 flex-1">
            <p className="telex text-[9px] text-brand-700">
              <span
                aria-hidden
                className="mr-1.5 inline-block h-[6px] w-[6px] bg-current align-middle"
              />
              Cookies
            </p>
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-neutral-600">
              We measure basic reach anonymously and cookie-free — nothing is
              stored on your device, so no consent is needed. With{" "}
              <em>Accept</em> you additionally allow privacy-friendly, EU-hosted
              cookies for cross-session insight (PostHog) to improve changemaker
              — no ads, no third-party ad-tracking. <em>Reject</em> turns all
              analytics off.{" "}
              <Link
                href="/cookies"
                className="font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800"
              >
                Cookie policy
              </Link>
              .
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={() => choose("denied")}
              className="inline-flex h-10 items-center justify-center rounded-md border border-neutral-300 px-4 text-sm font-semibold text-ink transition-colors hover:border-neutral-400 hover:bg-neutral-50"
            >
              Reject non-essential
            </button>
            <button
              type="button"
              onClick={() => choose("granted")}
              className="inline-flex h-10 items-center justify-center rounded-md bg-brand-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
